import { CrudRecord } from '../crud/types';
import { itemStockQty, lowStockItems, num } from './kpis';

export type InsightSeverity = 'info' | 'success' | 'warning' | 'error';
export type InsightType = 'insight' | 'alert';

export type BusinessSnapshot = {
  period: { start: string; end: string };
  sales: number;
  transactions: number;
  previousSales: number;
  previousTransactions: number;
  salesChangePercent: number;
  refunds: number;
  refundCount: number;
  refundRate: number;
  cashDifference: number;
  lowStockCount: number;
  outOfStockCount: number;
  topProduct: { name: string; quantity: number; amount: number } | null;
  peakHour: { hour: number; amount: number } | null;
};

export type BusinessMessage = {
  code: string;
  type: InsightType;
  severity: InsightSeverity;
  priority: number;
  title: string;
  description: string;
  detail: string;
  metricValues: Record<string, number | string>;
  action: string;
  timestamp: string;
  to: string;
};

function dateOf(row: CrudRecord) {
  const value = row.created_at ?? row.date;
  const date = value ? new Date(String(value)) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
}

function dayStart(daysAgo: number) {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - daysAgo);
  return date;
}

function lineQuantity(line: Record<string, unknown>) {
  return num(line.quantity ?? line.qty ?? 0);
}

function saleAmount(row: CrudRecord) {
  return num(row.grand_total ?? row.total ?? 0);
}

export function buildBusinessSnapshot(rows: {
  sales: CrudRecord[];
  returnOrders: CrudRecord[];
  items: CrudRecord[];
  registerRecords: CrudRecord[];
}): BusinessSnapshot {
  const currentStart = dayStart(6);
  const previousStart = dayStart(13);
  const currentSales = rows.sales.filter((sale) => !sale.draft && (dateOf(sale)?.getTime() ?? 0) >= currentStart.getTime());
  const previousSales = rows.sales.filter((sale) => {
    const time = dateOf(sale)?.getTime() ?? 0;
    return !sale.draft && time >= previousStart.getTime() && time < currentStart.getTime();
  });
  const currentReturns = rows.returnOrders.filter((order) => !order.draft && (dateOf(order)?.getTime() ?? 0) >= currentStart.getTime());
  const productBuckets = new Map<string, { quantity: number; amount: number }>();
  const hourBuckets = new Map<number, number>();
  currentSales.forEach((sale) => {
    const hour = dateOf(sale)?.getHours();
    if (hour != null) hourBuckets.set(hour, (hourBuckets.get(hour) ?? 0) + saleAmount(sale));
    const lines = Array.isArray(sale.items) ? sale.items : [];
    lines.forEach((raw) => {
      if (!raw || typeof raw !== 'object') return;
      const line = raw as Record<string, unknown>;
      const name = String(line.name ?? line.code ?? line.item_id ?? 'Unknown item');
      const current = productBuckets.get(name) ?? { quantity: 0, amount: 0 };
      current.quantity += lineQuantity(line);
      current.amount += num(line.subtotal) || num(line.price) * lineQuantity(line);
      productBuckets.set(name, current);
    });
  });
  const topProductEntry = [...productBuckets.entries()].sort((a, b) => b[1].quantity - a[1].quantity)[0];
  const topHourEntry = [...hourBuckets.entries()].sort((a, b) => b[1] - a[1])[0];
  const low = lowStockItems(rows.items);
  const outOfStockCount = rows.items.filter((item) => item.is_stock !== false && item.is_stock !== 0 && itemStockQty(item) <= 0).length;
  const cashDifference = rows.registerRecords.reduce((total, record) => {
    if (!record.closed_at) return total;
    return total + (num(record.total_cash_submitted) - num(record.total_cash_amount));
  }, 0);
  const sales = currentSales.reduce((total, sale) => total + saleAmount(sale), 0);
  const previousSalesTotal = previousSales.reduce((total, sale) => total + saleAmount(sale), 0);
  const refunds = currentReturns.reduce((total, order) => total + saleAmount(order), 0);

  return {
    period: { start: currentStart.toISOString(), end: new Date().toISOString() },
    sales,
    transactions: currentSales.length,
    previousSales: previousSalesTotal,
    previousTransactions: previousSales.length,
    salesChangePercent: previousSalesTotal > 0 ? ((sales - previousSalesTotal) / previousSalesTotal) * 100 : 0,
    refunds,
    refundCount: currentReturns.length,
    refundRate: sales > 0 ? (refunds / sales) * 100 : 0,
    cashDifference,
    lowStockCount: low.length,
    outOfStockCount,
    topProduct: topProductEntry ? { name: topProductEntry[0], ...topProductEntry[1] } : null,
    peakHour: topHourEntry ? { hour: topHourEntry[0], amount: topHourEntry[1] } : null
  };
}

export function buildInsights(snapshot: BusinessSnapshot, now = new Date()): BusinessMessage[] {
  const messages: BusinessMessage[] = [];
  const add = (message: Omit<BusinessMessage, 'timestamp' | 'detail'>) => messages.push({ ...message, detail: message.description, timestamp: now.toISOString() });
  if (snapshot.previousTransactions >= 3 && snapshot.salesChangePercent >= 10) {
    add({ code: 'SALES_UP', type: 'insight', severity: 'success', priority: 40, title: 'Sales are up', description: `Sales increased ${snapshot.salesChangePercent.toFixed(1)}% versus the previous 7-day period.`, metricValues: { sales: snapshot.sales, changePercent: snapshot.salesChangePercent }, action: 'Keep the momentum and review your best-performing products.', to: 'sales' });
  }
  if (snapshot.previousTransactions >= 3 && snapshot.salesChangePercent <= -10) {
    add({ code: 'SALES_DOWN', type: 'alert', severity: 'warning', priority: 70, title: 'Sales are below the previous period', description: `Sales decreased ${Math.abs(snapshot.salesChangePercent).toFixed(1)}% versus the previous 7-day period.`, metricValues: { sales: snapshot.sales, changePercent: snapshot.salesChangePercent }, action: 'Review daily sales, staffing, and the busiest register periods.', to: 'sales' });
  }
  if (snapshot.refundCount >= 3 && snapshot.refunds >= 50 && snapshot.refundRate >= 2) {
    add({ code: 'REFUNDS_HIGH', type: 'alert', severity: 'warning', priority: 70, title: 'Refunds are higher than usual', description: `Refunds represent ${snapshot.refundRate.toFixed(1)}% of sales.`, metricValues: { refunds: snapshot.refunds, refundRate: snapshot.refundRate, refundCount: snapshot.refundCount }, action: 'Review recent refund transactions and their reasons.', to: 'return-orders' });
  }
  if (Math.abs(snapshot.cashDifference) >= 1) {
    add({ code: 'CASH_DIFFERENCE', type: 'alert', severity: Math.abs(snapshot.cashDifference) >= 20 ? 'error' : 'warning', priority: Math.abs(snapshot.cashDifference) >= 20 ? 100 : 70, title: 'Cash difference detected', description: `Closed register sessions differ by ${snapshot.cashDifference.toFixed(2)}.`, metricValues: { cashDifference: snapshot.cashDifference }, action: 'Review the affected register closing records.', to: 'register-records' });
  }
  if (snapshot.outOfStockCount > 0) {
    add({ code: 'OUT_OF_STOCK', type: 'alert', severity: 'error', priority: 100, title: 'Products are out of stock', description: `${snapshot.outOfStockCount} product${snapshot.outOfStockCount === 1 ? '' : 's'} have no available stock.`, metricValues: { count: snapshot.outOfStockCount }, action: 'Replenish stock or review inventory adjustments.', to: 'items' });
  }
  if (snapshot.lowStockCount > 0) {
    add({ code: 'LOW_STOCK', type: 'alert', severity: 'warning', priority: 70, title: 'Products are below reorder level', description: `${snapshot.lowStockCount} product${snapshot.lowStockCount === 1 ? '' : 's'} are at or below their alert quantity.`, metricValues: { count: snapshot.lowStockCount }, action: 'Review reorder quantities and create a purchase.', to: 'items' });
  }
  if (snapshot.topProduct && snapshot.transactions >= 3) {
    add({ code: 'TOP_PRODUCT', type: 'insight', severity: 'success', priority: 40, title: 'Top-selling product', description: `${snapshot.topProduct.name} led the last 7 days with ${snapshot.topProduct.quantity} units sold.`, metricValues: { product: snapshot.topProduct.name, quantity: snapshot.topProduct.quantity, amount: snapshot.topProduct.amount }, action: 'Keep this product visible and check its stock level.', to: 'items' });
  }
  if (snapshot.peakHour && snapshot.transactions >= 3) {
    add({ code: 'PEAK_HOUR', type: 'insight', severity: 'info', priority: 20, title: 'Peak sales hour', description: `The busiest sales hour was ${String(snapshot.peakHour.hour).padStart(2, '0')}:00–${String((snapshot.peakHour.hour + 1) % 24).padStart(2, '0')}:00.`, metricValues: { hour: snapshot.peakHour.hour, amount: snapshot.peakHour.amount }, action: 'Plan staffing and promotions around this period.', to: 'sales' });
  }
  const seen = new Set<string>();
  return messages
    .sort((a, b) => b.priority - a.priority || b.timestamp.localeCompare(a.timestamp))
    .filter((message) => !seen.has(message.code) && Boolean(seen.add(message.code)))
    .slice(0, 5);
}
